import { ICloneable } from '../../../../contracts/cloneable.js';
import { IDataComparable } from '../../../../contracts/data-comparable.js';
import { JSONUtils } from '../../../../utility/json-utils.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { isSameData, isSameList } from '../../../dto/data-model-utils.js';
import {
  SerializedData, SerializationContext, DataModelBuilder,
} from '../../../dto/data-model.js';
import { OpenAccessPass } from '../../open-access-pass/open-access-pass.js';
import { AuditLogEntryType } from '../audit-log-entry-type.js';
import { AuditLogEntry } from '../audit-log-entry.js';

export class SeatedOpenAccessPassSwappedEntry extends AuditLogEntry {
  static readonly TYPE_ID: string = '3bcd1f41193d4b69bb06133b48a8e8d8';

  private constructor(
    public readonly before: OpenAccessPass,
    public readonly after: OpenAccessPass,
    public readonly addedSeats: SeatRef[],
    public readonly removedSeats: SeatRef[],
  ) {
    super();
  }

  get $typeId(): string {
    return SeatedOpenAccessPassSwappedEntry.TYPE_ID;
  }

  generateQueryData(): SerializedData {
    return {
      businessRef: this.after.businessRef.encode(),
      passId: this.after.uniqueId,
      orderId: this.after.orderId,
    };
  }

  get type(): AuditLogEntryType {
    return AuditLogEntryType.SeatedEventOpenAccessPassSwap;
  }

  static create(args: {
    before: OpenAccessPass,
    after: OpenAccessPass,
    addedSeats: SeatRef[],
    removedSeats: SeatRef[],
  }): SeatedOpenAccessPassSwappedEntry {
    return new SeatedOpenAccessPassSwappedEntry(
        args.before,
        args.after,
        args.addedSeats,
        args.removedSeats,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): SeatedOpenAccessPassSwappedEntry {
    return this.create({
      before: context.fromData<OpenAccessPass>(MapUtils.getRecord(map, 'before')),
      after: context.fromData<OpenAccessPass>(MapUtils.getRecord(map, 'after')),
      addedSeats: MapUtils.getList<SeatRef>(
          map,
          'addedSeats',
          (data) => SeatRef.fromMap(context, JSONUtils.decodeRecord(data))),
      removedSeats: MapUtils.getList<SeatRef>(
          map,
          'removedSeats',
          (data) => SeatRef.fromMap(context, JSONUtils.decodeRecord(data))),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      before: context.toData(this.before),
      after: context.toData(this.after),
      addedSeats: this.addedSeats.map((seatRef) => seatRef.toMap(context)),
      removedSeats: this.removedSeats.map((seatRef) => seatRef.toMap(context)),
    };
  }

  clone(): this {
    return SeatedOpenAccessPassSwappedEntry.create({
      before: this.before.clone(),
      after: this.after.clone(),
      addedSeats: this.addedSeats.map((seatRef) => seatRef.clone()),
      removedSeats: this.removedSeats.map((seatRef) => seatRef.clone()),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.before, this.before) &&
      isSameData(other.after, this.after) &&
      isSameList(other.addedSeats, this.addedSeats, isSameData) &&
      isSameList(other.removedSeats, this.removedSeats, isSameData);
  }
}

export class SeatedOpenAccessPassSwappedEntryBuilder extends DataModelBuilder<SeatedOpenAccessPassSwappedEntry> {
  build(context: SerializationContext, map: SerializedData): SeatedOpenAccessPassSwappedEntry {
    return SeatedOpenAccessPassSwappedEntry.fromMap(context, map);
  }
}

export class SeatRef implements ICloneable, IDataComparable {
  private constructor(
    public readonly eventId: string,
    public readonly seatId: number,
  ) {}

  static create(args: {
    eventId: string,
    seatId: number,
  }): SeatRef {
    return new SeatRef(
        args.eventId,
        args.seatId,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): SeatRef {
    return this.create({
      eventId: MapUtils.getString(map, 'eventId'),
      seatId: MapUtils.getNumber(map, 'seatId'),
    });
  }

  toMap(_context: SerializationContext): SerializedData {
    return {
      eventId: this.eventId,
      seatId: this.seatId,
    };
  }

  clone(): this {
    return SeatRef.create({
      eventId: this.eventId,
      seatId: this.seatId,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.eventId === this.eventId &&
        other.seatId === this.seatId;
  }
}
